import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';

interface VerifyLinkResponse { success: boolean; message?: string; }

type VerificationState = 'loading' | 'success' | 'error';

const VerifyLinkPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { reloadAuthStatus } = useAuth();
  const [state, setState] = useState<VerificationState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Prevent double-execution (React 18 StrictMode runs useEffect twice in development)
    if (verificationAttempted.current) {
      return;
    }
    verificationAttempted.current = true;

    const verifyLink = async () => {
      const token = searchParams.get('t');
      const hash = searchParams.get('h');

      if (!token || !hash) {
        setState('error');
        setErrorMessage('Invalid verification link. Please request a new verification code.');
        return;
      }

      try {
        const response = await api.get<VerifyLinkResponse>(`/api/auth/verify-link?t=${encodeURIComponent(token)}&h=${encodeURIComponent(hash)}`);

        if (response.success) {
          setState('success');

          // Update AuthContext to reflect the new authentication state
          // This prevents redirect loops when navigating to /jobs
          await reloadAuthStatus();

          // Redirect to jobs page after a brief delay
          setTimeout(() => {
            navigate('/jobs');
          }, 1500);
        } else {
          setState('error');
          setErrorMessage(response.message || 'Verification failed. Please try again.');
        }
      } catch (error) {
        console.error('Error verifying magic link:', error);
        setState('error');
        setErrorMessage('An error occurred during verification. Please try again or request a new code.');
      }
    };

    verifyLink();
  }, [searchParams, navigate, reloadAuthStatus]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mx: 'auto', textAlign: 'center' }}>
          {state === 'loading' && (
            <>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Verifying Your Login
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your authentication...
              </Typography>
            </>
          )}

          {state === 'success' && (
            <>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="success.main">
                Verification Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You have been successfully authenticated.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Redirecting you to your dashboard...
              </Typography>
            </>
          )}

          {state === 'error' && (
            <>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="error.main">
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ my: 3 }}>
                {errorMessage}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This link may have expired or already been used. Each verification link can only be used once and expires after 10 minutes.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                fullWidth
              >
                Return to Login
              </Button>
            </>
          )}
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default VerifyLinkPage;
